import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { forwardRef, useMemo, Fragment as Fragment$1 } from 'react';
import { styled, css } from 'styled-components';
import { ancestorToLocationSelectorNodePathItem } from '../../../../../resources/components/DestinationSelector/utils.js';
import { detailsToAttributeMaps } from '../../../../resource-details.config.js';
import { useQueryResourceAncestors } from '../../../../queries/useQueryResourceAncestors.js';
import { useResourceDataContextSafe } from '../../../context/resourceDataContext.js';
import { isValidAttribute, attributeDateFormatter } from '../utils.js';
import { Flex } from '@fabric-app/ui/components/Flex';
import { mediaMobile, mediaHover } from '@fabric-app/ui/constants/styled-utils';
import { ScrollArea } from '@fabric-app/ui/components/ScrollArea';
import { DD as DD$1, DLItem as DLItem$1, DL, DT } from '@fabric-app/ui/components/Typography';
import { Spinner } from '@fabric-app/ui/components/Spinner';
import { parseIntOrString, formatFileSize } from '@fabric-app/utils/number';
import { capitalizeFirstLetter, capitalizeFirstWordIfLowercase } from '@fabric-app/utils/text';
import { generateKind } from '@fabric-app/utils/files';
import Divider from '@fabric-app/ui/components/Divider';
import { useContextAccessToken } from '../../../../../permission/contexts/AnonShareTokenContext.js';
import { useQueryUserInfo } from '../../../../../user/queries/useQueryUserInfo.js';
import AvatarUser from '../../../../../user/components/AvatarUser/AvatarUser.js';
import Badge from '@fabric-app/ui/components/Badge';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { getTextColor } from '@fabric-app/utils/color';
import { renderCssBasedOnColorScheme } from '@fabric-app/ui/theme/renderCssBasedOnColorScheme';
import useCopyToClipboard from '@fabric-app/hooks/useCopyToClipboard';
import { toast } from '../../../../../store/alerts.js';

const DD = styled(DD$1).withConfig({
  displayName: "ExtraPanelInfo__DD",
  componentId: "sc-37d51e88-0"
})([
  `user-select:text;`
]);
const DLItem = styled(DLItem$1).withConfig({
  displayName: "ExtraPanelInfo__DLItem",
  componentId: "sc-37d51e88-1"
})([
  `gap:0.25rem;`
]);
const Container = styled(Flex).attrs((p) => {
  return {
    direction: "column",
    gap: "elementsContainer",
    ...p
  };
}).withConfig({
  displayName: "ExtraPanelInfo__Container",
  componentId: "sc-37d51e88-2"
})([
  `padding:10px 12px;font-size:0.9rem;`,
  `{padding:14px;}`
], mediaMobile);
const StyledScrollArea = styled(ScrollArea.Default).withConfig({
  displayName: "ExtraPanelInfo__StyledScrollArea",
  componentId: "sc-37d51e88-3"
})([
  `display:grid;[data-radix-scroll-area-viewport] > div{display:grid;}`
]);
const isAttributesProcessing = (details) => {
  const processingStatus = details.jobsProcessing.attributes || null;
  if (!processingStatus || [
    "processing",
    "pending"
  ].includes(processingStatus)) {
    return true;
  }
  return false;
};
const Pill = styled(Badge.Static).attrs((p) => {
  return {
    ...p,
    size: "large"
  };
}).withConfig({
  displayName: "ExtraPanelInfo__Pill",
  componentId: "sc-37d51e88-4"
})([
  ``,
  ` font-weight:500;&[data-prefix]{&::before{content:attr(data-prefix);font-weight:500;opacity:0.5;margin-right:0.25rem;color:inherit;}}cursor:pointer;user-select:none;`,
  `{&:hover:not(:disabled){opacity:0.8;}}`
], ({ color }) => color === "default" ? renderCssBasedOnColorScheme({
  light: css([
    `background-color:`,
    `;color:`,
    `;`
  ], cssVar["color-bg-tertiary"], cssVar["color-text-primary"]),
  dark: css([
    `background-color:`,
    `;color:`,
    `;`
  ], cssVar["color-bg-quinary"], cssVar["color-text-primary"])
}) : css([
  `background-color:`,
  `;color:`,
  `;`
], color, getTextColor(color)), mediaHover);
const ExtraPanelInfo = /* @__PURE__ */ forwardRef((props, ref) => {
  const { data: user } = useQueryUserInfo();
  const { resource } = useResourceDataContextSafe();
  const accessToken = useContextAccessToken();
  const { accessibleAncestors } = useQueryResourceAncestors(resource?.id, {
    // we don't want to fetch this in public views
    enabled: !accessToken
  });
  const resourcePath = useMemo(() => accessibleAncestors.map(ancestorToLocationSelectorNodePathItem), [
    accessibleAncestors
  ]);
  const attributes = detailsToAttributeMaps(resource).filter(isValidAttribute);
  const size = "size" in resource ? parseIntOrString(resource.size ?? 0) : 0;
  const extension = resource.extension || "";
  const mimeType = resource.mimeType;
  const createdAt = resource.createdAt;
  const modifiedAt = resource.modifiedAt;
  const isProcessing = isAttributesProcessing(resource);
  const [, copyToClipboard] = useCopyToClipboard();
  const handleCopyToClipboard = (value) => {
    if (copyToClipboard(value)) toast({
      id: "attribute-copy",
      content: "Copied to clipboard"
    });
  };
  return /* @__PURE__ */ jsx(StyledScrollArea, {
    expandVertically: true,
    ref,
    ...props,
    children: /* @__PURE__ */ jsxs(Container, {
      children: [
        /* @__PURE__ */ jsxs(DL, {
          children: [
            accessToken || accessibleAncestors.length < 1 ? null : /* @__PURE__ */ jsxs(DLItem, {
              children: [
                /* @__PURE__ */ jsx(DT, {
                  children: "Path"
                }),
                /* @__PURE__ */ jsx(DD, {
                  children: /* @__PURE__ */ jsxs(Flex, {
                    alignItems: "center",
                    flexWrap: "wrap",
                    children: [
                      resource.root?.folder.user && resource.root?.folder.user.id !== user?.id && /* @__PURE__ */ jsxs(Fragment, {
                        children: [
                          /* @__PURE__ */ jsx(AvatarUser, {
                            user: resource.root?.folder.user,
                            size: 16,
                            disableTooltip: true
                          }),
                          " / "
                        ]
                      }),
                      resourcePath.map((p) => p.title).join(" / ")
                    ]
                  })
                })
              ]
            }),
            /* @__PURE__ */ jsxs(DLItem, {
              children: [
                /* @__PURE__ */ jsx(DT, {
                  children: "Size"
                }),
                /* @__PURE__ */ jsxs(DD, {
                  children: [
                    parseIntOrString(size).toLocaleString("en-US", {
                      style: "decimal",
                      maximumFractionDigits: 0,
                      useGrouping: true
                    }),
                    " ",
                    "bytes (",
                    formatFileSize(size),
                    " on disk)",
                    " "
                  ]
                })
              ]
            }),
            /* @__PURE__ */ jsxs(DLItem, {
              children: [
                /* @__PURE__ */ jsx(DT, {
                  children: "Kind"
                }),
                /* @__PURE__ */ jsxs(DD, {
                  children: [
                    mimeType ? generateKind(mimeType, extension) : capitalizeFirstLetter(resource.kind),
                    " "
                  ]
                })
              ]
            }),
            createdAt && /* @__PURE__ */ jsxs(DLItem, {
              children: [
                /* @__PURE__ */ jsx(DT, {
                  children: "Created"
                }),
                /* @__PURE__ */ jsx(DD, {
                  children: attributeDateFormatter(createdAt)
                })
              ]
            }),
            modifiedAt && /* @__PURE__ */ jsxs(DLItem, {
              children: [
                /* @__PURE__ */ jsx(DT, {
                  children: "Modified"
                }),
                /* @__PURE__ */ jsx(DD, {
                  children: attributeDateFormatter(modifiedAt)
                })
              ]
            })
          ]
        }),
        isProcessing && /* @__PURE__ */ jsxs(Fragment, {
          children: [
            /* @__PURE__ */ jsx(Divider, {}),
            /* @__PURE__ */ jsx(Flex, {
              justifyContent: "center",
              alignItems: "center",
              style: {
                padding: "1rem"
              },
              children: /* @__PURE__ */ jsx(Spinner, {
                size: 24
              })
            })
          ]
        }),
        attributes.length > 0 && /* @__PURE__ */ jsxs(Fragment, {
          children: [
            /* @__PURE__ */ jsx(Divider, {}),
            /* @__PURE__ */ jsx(DL, {
              children: attributes.map(({ label, value, divider }) => /* @__PURE__ */ jsxs(Fragment$1, {
                children: [
                  divider && /* @__PURE__ */ jsx(Divider, {}),
                  /* @__PURE__ */ jsxs(DLItem, {
                    children: [
                      /* @__PURE__ */ jsx(DT, {
                        children: label
                      }),
                      /* @__PURE__ */ jsx(DD, {
                        children: Array.isArray(value) ? /* @__PURE__ */ jsx(Flex, {
                          gap: "element",
                          flexWrap: "wrap",
                          children: value.map((v) => /* @__PURE__ */ jsx(Pill, {
                            color: v.color,
                            "data-prefix": v.prefix,
                            onClick: () => handleCopyToClipboard(v.label),
                            children: capitalizeFirstWordIfLowercase(v.label)
                          }, v.label))
                        }) : value
                      })
                    ]
                  })
                ]
              }, label))
            })
          ]
        })
      ]
    })
  });
});
ExtraPanelInfo.displayName = "ExtraPanelInfo";

export { ExtraPanelInfo };
//# sourceMappingURL=ExtraPanelInfo.js.map
